import {
  BRIDGE_SEATS,
  type BridgeCard,
  type BridgeSeat,
  BIG_CARDS_MIN_RANK,
  HEARTS_TARGET,
  type HeartsGame,
  type HeartsHandResult,
  type HeartsRobotLevel,
  type HeartsTotals,
  NEW_HEARTS_TOTALS,
  SINGLE_PLAYER,
  addHeartsHand,
  heartsChooseCard,
  heartsChoosePass,
  heartsGameWinner,
  heartsRules,
  heartsTarget,
  heartsHandResult,
  heartsLegalPlays,
  heartsPlay,
  heartsSubmitPass,
  heartsViewFor,
  newHeartsGame,
  seededRandom,
} from '@strategy4kids/game-engine';
import { create } from 'zustand';
import { rememberBigCards, resolveBigCards } from '../lib/big-cards.js';
import type { AppQuery } from '../lib/query.js';
import { setAmbientMood } from '../services/audio.js';
import { cueCardPlay, cueDeal, cueRoundOrGame, cueSweep, cueTurn, type RoundOutcome } from './sfx-cues.js';
import { CHAT_BRIDGE, chatGameEnd, chatRound, chatterNewGame, extremeSeat, robotsOf } from './table-chatter.js';

/**
 * The Hearts store (single device: you in South, three robots). Its own store, like Bridge's and Whist's. Robots decide
 * from `heartsViewFor` only: they pick their passes and play from their own view.
 *
 * A game is a run of hands. Points are taken from tricks (a heart is 1, the queen of spades 13) and the LOWEST total wins
 * once anyone reaches 100. Points are only a score: nothing is staked.
 */
export const HUMAN_SEAT: BridgeSeat = 'S';

/** Pause before a robot acts so a kid can follow the trick. Zero in tests and with `?engine=fake`. */
const ROBOT_DELAY_MS = 650;

export interface HeartsState {
  game: HeartsGame | null;
  result: HeartsHandResult | null;
  /** Hands dealt so far in this game, counting the current one (0 = no game). */
  handNumber: number;
  totals: HeartsTotals;
  /** The score that ends the game: `HEARTS_TARGET`, or `?target=` when the page was opened with one (tests and audits). */
  target: number;
  gameWinner: BridgeSeat | null;
  strength: HeartsRobotLevel;
  fourColour: boolean;
  /** Big Cards: the 28-card deck of 8 to Ace (7 cards each, pass 2, game to 50). Off by default. */
  bigCards: boolean;
  seed: number | null;
  query: AppQuery | null;
  /** The cards the human has ticked to pass (up to three, or two with Big Cards) before confirming. */
  selected: BridgeCard[];
  start(query: AppQuery, seed?: number): void;
  nextHand(): void;
  newGame(): void;
  stop(): void;
  /** Tick or untick a card to pass (only while choosing a pass). */
  togglePass(card: BridgeCard): void;
  /** Hand on the ticked cards. */
  confirmPass(): void;
  play(card: BridgeCard): void;
  setStrength(strength: HeartsRobotLevel): void;
  setFourColour(on: boolean): void;
  /** Switch the deck; restarts the game from the same seed, like changing the table size in Rummy. */
  setBigCards(on: boolean): void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
const clearTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

const freshSeed = () => Math.floor(Math.random() * 2 ** 31);
/** Each hand of a game has its own seed derived from the game's, so a seeded game replays exactly. */
export const handSeed = (seed: number, handIndex: number): number => (seed + Math.imul(handIndex, 7919)) >>> 0;
const same = (a: BridgeCard, b: BridgeCard) => a.suit === b.suit && a.rank === b.rank;

export const useHeartsStore = create<HeartsState>((set, get) => {
  /** Take a new game state; when it ends the hand, score it, and when that ends the game, say so. */
  const advance = (game: HeartsGame) => {
    const before = get().game;
    // A card reaching the table is heard from the seat that played it (spec P2: seat panning).
    if (before?.phase === 'play') {
      cueCardPlay(before.turn);
      cueSweep(before, game);
    }
    const result = heartsHandResult(game);
    if (!result) {
      set({ game });
      cueTurn(before?.phase === 'play' ? before.turn : null, game.phase === 'play' ? game.turn : null, HUMAN_SEAT);
      return;
    }
    const totals = addHeartsHand(get().totals, result);
    const winner = heartsGameWinner(totals, get().target);
    set({ game, result, totals, gameWinner: winner, selected: [] });
    const mine = result.scored[HUMAN_SEAT];
    const best = Math.min(...BRIDGE_SEATS.map((s) => result.scored[s]));
    const others = BRIDGE_SEATS.filter((s) => s !== HUMAN_SEAT);
    const hand: RoundOutcome = mine === best ? (others.some((s) => result.scored[s] === best) ? 'draw' : 'won') : 'lost';
    cueRoundOrGame(winner ? (winner === HUMAN_SEAT ? 'won' : 'lost') : hand, winner !== null);
    // Table talk (public facts only: the scored hand). Taking a trick is a penalty in Hearts, so no trick lines here.
    if (winner) chatGameEnd([winner], robotsOf(BRIDGE_SEATS), CHAT_BRIDGE);
    else chatRound(extremeSeat(result.scored, robotsOf(BRIDGE_SEATS), 'best', true), extremeSeat(result.scored, robotsOf(BRIDGE_SEATS), 'worst', true), CHAT_BRIDGE);
  };

  /** Let the robots act: choose their passes, or play, after a short pause; stops at the human's move or the end of the hand. */
  const driveRobots = () => {
    clearTimer();
    const { game, query, strength } = get();
    if (!game || game.phase === 'done') return;
    const delay = query?.engine === 'fake' ? 0 : ROBOT_DELAY_MS;
    if (game.phase === 'pass') {
      const pending = BRIDGE_SEATS.filter((s) => game.controllers[s] === 'ai' && game.passes[s] === undefined);
      if (pending.length === 0) return;
      timer = setTimeout(() => {
        let g = get().game;
        if (!g || g.phase !== 'pass') return;
        for (const seat of pending) {
          if (g.passes[seat] !== undefined) continue;
          g = heartsSubmitPass(g, seat, heartsChoosePass(heartsViewFor(g, seat), strength, seededRandom(g.seed + seat.charCodeAt(0))));
        }
        advance(g);
        driveRobots();
      }, delay);
      return;
    }
    if (game.controllers[game.turn] !== 'ai') return;
    timer = setTimeout(() => {
      const current = get().game;
      if (!current || current.phase !== 'play' || current.controllers[current.turn] !== 'ai') return;
      const random = seededRandom(current.seed + current.tricks.length * 4 + current.trick.length);
      const seat = current.turn;
      advance(heartsPlay(current, heartsChooseCard(heartsViewFor(current, seat), strength, random)));
      driveRobots();
    }, delay);
  };

  const deal = (handIndex: number) => {
    clearTimer();
    const { seed, bigCards } = get();
    if (seed === null) return;
    const game = newHeartsGame({ seed: handSeed(seed, handIndex), minRank: bigCards ? BIG_CARDS_MIN_RANK : 2, handNumber: handIndex, dealer: BRIDGE_SEATS[handIndex % 4] as BridgeSeat, controllers: SINGLE_PLAYER });
    set({ game, result: null, handNumber: handIndex + 1, selected: [] });
    cueDeal();
    cueTurn(null, game.phase === 'pass' ? HUMAN_SEAT : game.phase === 'play' ? game.turn : null, HUMAN_SEAT);
    driveRobots();
  };

  const begin = (query: AppQuery, seed: number) => {
    clearTimer();
    chatterNewGame();
    set({ seed, query, target: query.target ?? heartsTarget(get().bigCards ? BIG_CARDS_MIN_RANK : 2), totals: NEW_HEARTS_TOTALS, gameWinner: null, result: null, handNumber: 0, selected: [] });
    deal(0);
  };

  return {
    game: null,
    result: null,
    handNumber: 0,
    totals: NEW_HEARTS_TOTALS,
    target: HEARTS_TARGET,
    gameWinner: null,
    strength: 'easy',
    fourColour: false,
    bigCards: false,
    seed: null,
    query: null,
    selected: [],
    start: (query, seed) => {
      setAmbientMood('cards');
      set({ bigCards: resolveBigCards('hearts', query.bigCards) });
      begin(query, seed ?? query.deal ?? freshSeed());
    },
    nextHand: () => {
      const { result, gameWinner: winner, handNumber } = get();
      if (!result || winner) return;
      deal(handNumber);
    },
    newGame: () => {
      const { query } = get();
      if (query) begin(query, query.deal ?? freshSeed());
    },
    stop: () => {
      clearTimer();
      set({ game: null, result: null, seed: null, handNumber: 0, totals: NEW_HEARTS_TOTALS, target: HEARTS_TARGET, gameWinner: null, selected: [] });
    },
    togglePass: (card) => {
      const { game, selected } = get();
      if (!game || game.phase !== 'pass' || game.passes[HUMAN_SEAT] !== undefined) return;
      if (!game.hands[HUMAN_SEAT].some((c) => same(c, card))) return;
      if (selected.some((c) => same(c, card))) set({ selected: selected.filter((c) => !same(c, card)) });
      else if (selected.length < heartsRules(game.minRank).passCount) set({ selected: [...selected, card] });
    },
    confirmPass: () => {
      const { game, selected } = get();
      if (!game || game.phase !== 'pass' || game.passes[HUMAN_SEAT] !== undefined || selected.length !== heartsRules(game.minRank).passCount) return;
      set({ selected: [] });
      advance(heartsSubmitPass(game, HUMAN_SEAT, selected));
      driveRobots();
    },
    play: (card) => {
      const { game } = get();
      if (!game || game.phase !== 'play' || game.turn !== HUMAN_SEAT) return;
      if (!heartsLegalPlays(game).some((c) => same(c, card))) return;
      // Only a card that reaches the table is heard (in `advance`); an illegal tap is silent.
      advance(heartsPlay(game, card));
      driveRobots();
    },
    setStrength: (strength) => set({ strength }),
    setFourColour: (fourColour) => set({ fourColour }),
    setBigCards: (bigCards) => {
      rememberBigCards('hearts', bigCards);
      set({ bigCards });
      const { query, seed } = get();
      if (query && seed !== null) begin(query, seed);
    },
  };
});

export { HEARTS_TARGET };
